import React, { useState, useRef, useCallback } from 'react';
import { ChevronDown, Stop, Copy, Checkmark } from '@core/icons-react';
import './Tag.scss';
import { LoadingIndicator } from '../../indicators';
import { OptionDropdown } from '../../dropdown/OptionDropdown';
import { DateDropdown } from '../../dropdown/DateDropdown';
import { ActionDropdown, type ActionDropdownOption } from '../../dropdown/ActionDropdown';
import { SkeletonBar } from '../../../skeletons';
import { useDropdownState } from '../../shared/hooks/useDropdownState';
import { useInteractiveState } from '../../shared/hooks/useInteractiveState';
import { useCopyToClipboard } from '../../shared/hooks/useCopyToClipboard';
import { cx } from '../../shared/utils/classNames';
import { getTagSkeletonProps } from '../../shared/utils/skeletonHelpers';

// ============================================
// Segment types
// ============================================

export type TagSeparatorStyle = 'dot' | 'line';
export type TagSegmentSize = number | CoreTagSize;

export type TagTextSegment = {
  type: 'text';
  value: string;
  bold?: boolean;
  color?: string;
  size?: TagSegmentSize;
};

export type TagIconSegment = {
  type: 'icon';
  icon: React.ComponentType<any>;
  size?: TagSegmentSize;
  color?: string;
};

export type TagSeparatorSegment = {
  type: 'separator';
  style: TagSeparatorStyle;
};

export type TagSegment = TagTextSegment | TagIconSegment | TagSeparatorSegment;

// ============================================
// Segment helpers (namespaced to avoid barrel collisions)
// ============================================

export const seg = {
  text: (
    value: string,
    opts?: Omit<TagTextSegment, 'type' | 'value'>,
  ): TagTextSegment => ({ type: 'text', value, ...opts }),

  icon: (
    iconComponent: React.ComponentType<any>,
    opts?: Omit<TagIconSegment, 'type' | 'icon'>,
  ): TagIconSegment => ({ type: 'icon', icon: iconComponent, ...opts }),

  dot: { type: 'separator', style: 'dot' } as TagSeparatorSegment,
  line: { type: 'separator', style: 'line' } as TagSeparatorSegment,
};

// ============================================
// Action types
// ============================================

export type TagActionFeedback = {
  icon?: React.ComponentType<any>;
  color?: CoreTagColor;
  duration?: number;
};

export type TagAction =
  | { type: 'copy'; value: string; label?: string; feedback?: TagActionFeedback }
  | { type: 'link'; href: string; external?: boolean }
  | { type: 'callback'; onClick: (e: React.MouseEvent) => void; icon?: React.ComponentType<any>; feedback?: TagActionFeedback };

const DEFAULT_COPY_FEEDBACK: TagActionFeedback = {
  icon: Checkmark,
  color: 'success',
  duration: 2000,
};

// ============================================
// Visual axis types
// ============================================

export type CoreTagSize = 'xxs' | 'xs' | 'sm' | 'md' | 'lg';
export type CoreTagShape = 'pill' | 'rect';
export type CoreTagFill = 'glass' | 'inverse' | 'inverse-subtle' | 'inverse-base' | 'transparent';
export type CoreTagGlassLevel = 'subtle' | 'base' | 'strong' | 'extra-strong';
export type CoreTagColor =
  | 'neutral' | 'success' | 'error' | 'warning' | 'info'
  | 'increase' | 'decrease'
  | 'aurelius-yellow' | 'aurelius-red' | 'aurelius-green' | 'aurelius-blue';
export type CoreTagBorder = 'none' | 'subtle' | 'default' | 'strong';
export type CoreTagTextColor = 'primary' | 'secondary' | 'disabled';
export type CoreTagDropdownType = 'none' | 'option' | 'date' | 'action';

// ============================================
// Template system
// ============================================

export type CoreTagTemplate = 'action-sm' | 'action-md' | 'header' | 'state' | 'http' | 'cell';

const TAG_TEMPLATES: Record<CoreTagTemplate, Partial<TagProps>> = {
  'action-sm': { fill: 'transparent', border: 'none', shape: 'rect', size: 'sm' },
  'action-md': { fill: 'transparent', border: 'none', shape: 'rect', size: 'md' },
  header:      { fill: 'transparent', border: 'subtle', shape: 'pill', size: 'lg' },
  state:       { fill: 'glass', glassLevel: 'subtle', shape: 'pill', size: 'sm' },
  http:        { shape: 'rect', size: 'xs', border: 'none', fill: 'glass' },
  cell:        { shape: 'pill', size: 'sm', fill: 'glass' },
};

const TEXT_COLOR_TOKENS: Record<CoreTagTextColor, string> = {
  primary: 'var(--core-text-primary)',
  secondary: 'var(--core-text-secondary)',
  disabled: 'var(--core-text-disabled)',
};

// ============================================
// Props
// ============================================

export interface TagProps {
  id?: string;
  segments?: TagSegment[];
  children?: React.ReactNode;

  // Template (applies preset defaults; explicit props override)
  template?: CoreTagTemplate;

  // Visual axes
  size?: CoreTagSize;
  iconSize?: CoreTagSize;
  shape?: CoreTagShape;
  fill?: CoreTagFill;
  glassLevel?: CoreTagGlassLevel;
  color?: CoreTagColor;
  border?: CoreTagBorder;

  // Custom colors (override token-based colors)
  fillColor?: string;
  textColor?: CoreTagTextColor | (string & {});

  // States
  active?: boolean;
  disabled?: boolean;
  loading?: boolean;
  skeleton?: boolean;
  visualOnly?: boolean;
  searching?: boolean;
  subtle?: boolean;

  // Interaction
  onClick?: (e?: React.MouseEvent) => void;
  onMouseEnter?: (e: React.MouseEvent) => void;
  onMouseLeave?: (e: React.MouseEvent) => void;
  action?: TagAction;

  // Display
  compact?: boolean;
  expandOnHover?: boolean;
  tabularNums?: boolean;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
  title?: string;
  flush?: 'left' | 'right' | 'both';

  // Dropdown support
  dropdown?: CoreTagDropdownType;
  showChevron?: boolean;
  isOpen?: boolean;
  dropdownOptions?: Array<{
    id: string;
    label?: string;
    parent?: string;
    disabled?: boolean;
    value?: string;
    unit?: string;
    icon?: React.ReactNode;
    children?: Array<{
      id: string;
      label: string;
      description?: string;
      disabled?: boolean;
      tag?: { label: string; variant?: string; };
    }>;
  }>;
  selectedItem?: string;
  selectedItems?: string[];
  onSelectionChange?: (id: string) => void;
  onMultiSelectionChange?: (ids: string[]) => void;
  multiSelect?: boolean;
  dropdownPosition?: 'left' | 'right';
  dropdownDirection?: 'down' | 'up';
  dropdownVariant?: 'subtle' | 'default' | 'strong' | 'glassmorphic';
  dropdownOffsetY?: number;
  dropdownMaxHeight?: string;
  allowUnselect?: boolean;
  dropdownRequireSelection?: boolean;
  dropdownDefaultValue?: string;

  // DateDropdown
  startDate?: Date;
  endDate?: Date;
  onDateRangeChange?: (start: Date, end: Date) => void;
  showPresets?: boolean;
  timezone?: string;

  // ActionDropdown
  viewOptions?: ActionDropdownOption[];
  actionOptions?: ActionDropdownOption[];
}

// ============================================
// Helpers
// ============================================

const DEFAULT_ICON_SIZE: Record<CoreTagSize, number> = {
  xxs: 8,
  xs: 10,
  sm: 12,
  md: 14,
  lg: 16,
};

const DEFAULT_FONT_SIZE: Record<CoreTagSize, number> = {
  xxs: 8,
  xs: 9,
  sm: 10,
  md: 11,
  lg: 12,
};

const TAG_PADDING_X: Record<CoreTagSize, string> = {
  xxs: '0.375rem',
  xs: '0.5rem',
  sm: '0.625rem',
  md: '0.75rem',
  lg: '0.875rem',
};

const resolveIconSize = (size: TagSegmentSize | undefined, fallback: number): number => {
  if (size === undefined) return fallback;
  if (typeof size === 'number') return size;
  return DEFAULT_ICON_SIZE[size];
};

const resolveFontSize = (size: TagSegmentSize | undefined): string | undefined => {
  if (size === undefined) return undefined;
  if (typeof size === 'number') return `${size}px`;
  return `${DEFAULT_FONT_SIZE[size]}px`;
};

const SEPARATOR_CHAR: Record<TagSeparatorStyle, string> = {
  dot: '\u00B7',
  line: '|',
};

const formatDate = (dateStr: Date | string): string => {
  if (!dateStr) return '';
  const date = dateStr instanceof Date ? dateStr : new Date(dateStr);
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = date.getUTCDate().toString().padStart(2, '0');
  const year = date.getUTCFullYear().toString().slice(-2);
  return `${month}/${day}/${year}`;
};

const hasTextSegment = (segs: TagSegment[]): boolean =>
  segs.some((s) => s.type === 'text');

const firstIconSegment = (segs: TagSegment[]): TagIconSegment | undefined =>
  segs.find((s): s is TagIconSegment => s.type === 'icon');

const getTextContent = (segs: TagSegment[]): string =>
  segs.filter((s): s is TagTextSegment => s.type === 'text').map((s) => s.value).join(' ');

function getActionIcon(action: TagAction): React.ComponentType<any> | undefined {
  switch (action.type) {
    case 'copy': return Copy;
    case 'callback': return action.icon;
    default: return undefined;
  }
}

function getActionFeedback(action: TagAction): TagActionFeedback | undefined {
  switch (action.type) {
    case 'copy': return { ...DEFAULT_COPY_FEEDBACK, ...action.feedback };
    case 'callback': return action.feedback;
    default: return undefined;
  }
}

// ============================================
// Component
// ============================================

export function Tag(props: TagProps) {
  // Resolve template defaults — explicit props always win
  const tpl = props.template ? TAG_TEMPLATES[props.template] : undefined;
  const {
    id,
    segments = [],
    children,
    template: _template,

    // Visual
    size = tpl?.size ?? 'md',
    iconSize,
    shape = tpl?.shape ?? 'pill',
    fill = tpl?.fill ?? 'glass',
    glassLevel = tpl?.glassLevel ?? 'base',
    color,
    border = tpl?.border,

    // Custom colors
    fillColor,
    textColor,

    // States
    active = false,
    disabled = false,
    loading = false,
    skeleton = false,
    visualOnly = false,
    searching = false,
    subtle = false,

    // Interaction
    onClick,
    onMouseEnter: externalMouseEnter,
    onMouseLeave: externalMouseLeave,
    action,

    // Display
    compact = false,
    expandOnHover = false,
    tabularNums = false,
    placeholder,
    className,
    style,
    title,
    flush,

    // Dropdown
    dropdown = 'none',
    showChevron = true,
    isOpen: initialOpen = false,
    dropdownOptions,
    selectedItem,
    selectedItems,
    onSelectionChange,
    onMultiSelectionChange,
    multiSelect = false,
    dropdownPosition = 'left',
    dropdownDirection = 'down',
    dropdownVariant = 'default',
    dropdownOffsetY,
    dropdownMaxHeight,
    allowUnselect = false,
    dropdownRequireSelection = false,
    dropdownDefaultValue,
    startDate,
    endDate,
    onDateRangeChange,
    showPresets = true,
    timezone = 'UTC',
    viewOptions = [],
    actionOptions = [],
  } = props;
  const { isOpen: dropdownOpen, setIsOpen: setDropdownOpen, toggleDropdown, dropdownRef: tagRef } = useDropdownState({
    initialOpen,
    closeOnClickOutside: true,
    disabled,
  });

  const { isHovered, isPressed, handlers: baseHandlers } = useInteractiveState({
    disabled,
    active,
  });

  const interactiveHandlers = {
    ...baseHandlers,
    onMouseEnter: (e: React.MouseEvent) => { baseHandlers.onMouseEnter(e); externalMouseEnter?.(e); },
    onMouseLeave: (e: React.MouseEvent) => { baseHandlers.onMouseLeave(e); externalMouseLeave?.(e); },
  };

  const { copy } = useCopyToClipboard();

  // Action feedback state
  const [feedbackActive, setFeedbackActive] = useState(false);
  const feedbackTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const triggerFeedback = useCallback((duration: number) => {
    if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
    setFeedbackActive(true);
    feedbackTimer.current = setTimeout(() => setFeedbackActive(false), duration);
  }, []);

  const hasDropdown = dropdown !== 'none';
  const isOptionDropdown = dropdown === 'option';
  const isDateDropdown = dropdown === 'date';
  const isActionDropdown = dropdown === 'action';
  const hasAction = !!action;
  const isInteractive = !disabled && !visualOnly && (!!onClick || hasDropdown || hasAction);
  const defaultIconSize = DEFAULT_ICON_SIZE[iconSize ?? size];

  // Resolve action visuals
  const actionFeedback = action ? getActionFeedback(action) : undefined;
  const actionBaseIcon = action ? getActionIcon(action) : undefined;
  const ActionIcon = feedbackActive && actionFeedback?.icon ? actionFeedback.icon : actionBaseIcon;
  const feedbackColor = feedbackActive && actionFeedback?.color ? actionFeedback.color : undefined;

  // Date dropdown auto-generates a text segment from the date range
  const effectiveSegments: TagSegment[] = isDateDropdown && startDate && endDate
    ? [{ type: 'text', value: `${formatDate(startDate)} - ${formatDate(endDate)} ${timezone}` }]
    : segments;

  // Auto-detect icon-only: single icon segment, no text segments
  const iconOnly = effectiveSegments.length === 1
    && effectiveSegments[0].type === 'icon'
    && !hasTextSegment(effectiveSegments);

  const showCompact = compact && hasDropdown && !iconOnly;
  const hasContent = effectiveSegments.length > 0 || !!placeholder || !!children;
  const isEmpty = effectiveSegments.length === 0 && !children;
  const compactIcon = showCompact ? firstIconSegment(effectiveSegments) : undefined;
  const compactTooltip = showCompact ? getTextContent(effectiveSegments) : undefined;
  const renderChevron = hasDropdown && showChevron && hasContent && !showCompact;

  const handleClick = (e: React.MouseEvent) => {
    if (disabled || visualOnly) return;

    if (action) {
      const fb = getActionFeedback(action);
      switch (action.type) {
        case 'copy':
          copy(action.value);
          if (fb) triggerFeedback(fb.duration ?? 2000);
          return;
        case 'link':
          if (action.external) {
            window.open(action.href, '_blank', 'noopener');
          } else {
            window.location.href = action.href;
          }
          return;
        case 'callback':
          action.onClick(e);
          if (fb) triggerFeedback(fb.duration ?? 2000);
          return;
      }
    }

    if (hasDropdown) toggleDropdown();
    if (onClick) {
      e.stopPropagation();
      onClick(e);
    }
  };

  // Effective color: feedback color overrides the prop color while active
  const effectiveColor = feedbackColor || color;

  const tagClasses = cx(
    'core-tag',
    `core-tag--${size}`,
    `core-tag--${shape}`,
    fill !== 'glass' && `core-tag--fill-${fill}`,
    fill === 'glass' && glassLevel !== 'base' && `core-tag--glass-${glassLevel}`,
    effectiveColor && `core-tag--color-${effectiveColor}`,
    border && `core-tag--border-${border}`,
    isInteractive && 'core-tag--interactive',
    !isInteractive && 'core-tag--static',
    isHovered && isInteractive && 'core-tag--hovered',
    isPressed && isInteractive && 'core-tag--pressed',
    active && 'core-tag--active',
    dropdownOpen && 'core-tag--open',
    disabled && 'core-tag--disabled',
    iconOnly && 'core-tag--icon-only',
    showCompact && 'core-tag--compact',
    expandOnHover && 'core-tag--expand-on-hover',
    searching && 'core-tag--searching',
    subtle && 'core-tag--subtle',
    tabularNums && 'core-tag--tabular-nums',
    isEmpty && placeholder && 'core-tag--placeholder',
    feedbackActive && 'core-tag--feedback',
    className,
  );

  const inlineStyle: React.CSSProperties = {
    ...style,
    ...(fillColor ? { backgroundColor: fillColor } : {}),
    ...(textColor ? { color: TEXT_COLOR_TOKENS[textColor as CoreTagTextColor] || textColor } : {}),
  };

  if (skeleton) {
    const skeletonProps = getTagSkeletonProps(iconOnly ? 'icon-only' : 'text-only', 'medium', compact);
    return <SkeletonBar {...skeletonProps} />;
  }

  // Render a single segment
  const renderSegment = (s: TagSegment, i: number) => {
    switch (s.type) {
      case 'text': {
        const fontSize = resolveFontSize(s.size);
        const textStyle = (s.color || fontSize)
          ? { ...(s.color ? { color: s.color } : {}), ...(fontSize ? { fontSize } : {}) }
          : undefined;
        return (
          <span
            key={i}
            className={cx('core-tag__segment core-tag__segment--text', s.bold && 'core-tag__segment--bold')}
            style={textStyle}
          >
            {s.value}
          </span>
        );
      }
      case 'icon': {
        const IconComp = searching ? Stop : s.icon;
        const sz = resolveIconSize(s.size, defaultIconSize);
        return (
          <span
            key={i}
            className="core-tag__segment core-tag__segment--icon"
            style={s.color ? { color: s.color } : undefined}
          >
            {iconOnly && searching ? (
              <span className="core-tag__icon-container">
                <span className={cx('core-tag__icon-crossfade', searching && 'core-tag__icon-crossfade--hidden')}>
                  <s.icon size={sz} />
                </span>
                <span className={cx('core-tag__icon-crossfade core-tag__icon-crossfade--stop', !searching && 'core-tag__icon-crossfade--hidden')}>
                  <Stop size={sz} />
                </span>
              </span>
            ) : (
              <IconComp size={sz} />
            )}
          </span>
        );
      }
      case 'separator':
        return (
          <span key={i} className={cx('core-tag__separator', `core-tag__separator--${s.style}`)}>
            {SEPARATOR_CHAR[s.style]}
          </span>
        );
    }
  };

  return (
    <div ref={tagRef} style={{
      display: 'inline-flex',
      position: 'relative',
      ...(flush === 'left' || flush === 'both' ? { marginLeft: `-${TAG_PADDING_X[size]}` } : {}),
      ...(flush === 'right' || flush === 'both' ? { marginRight: `-${TAG_PADDING_X[size]}` } : {}),
    }}>
      {React.createElement(
        visualOnly ? 'span' : 'button',
        {
          id,
          className: tagClasses,
          onClick: handleClick,
          ...(visualOnly ? {} : { disabled, type: 'button' as const }),
          title: title || (showCompact ? (compactTooltip || undefined) : undefined),
          style: Object.keys(inlineStyle).length > 0 ? inlineStyle : undefined,
          tabIndex: visualOnly ? -1 : undefined,
          ...(isInteractive ? interactiveHandlers : {}),
        },
        showCompact ? (
          <>
            {compactIcon && (
              <span className="core-tag__segment core-tag__segment--icon">
                {searching ? <Stop size={compactIcon.size ?? defaultIconSize} /> : <compactIcon.icon size={compactIcon.size ?? defaultIconSize} />}
              </span>
            )}
            {hasDropdown && showChevron && (
              loading ? (
                <LoadingIndicator className="core-tag__loading-spinner" size="small" color="current" />
              ) : (
                <span className={cx('core-tag__chevron', dropdownOpen && 'core-tag__chevron--open')}>
                  <ChevronDown size={12} />
                </span>
              )
            )}
          </>
        ) : (
          <>
            {effectiveSegments.length > 0
              ? effectiveSegments.map(renderSegment)
              : placeholder
                ? <span className="core-tag__segment core-tag__segment--text">{placeholder}</span>
                : null
            }

            {ActionIcon && (
              <span className={cx('core-tag__action-icon', feedbackActive && 'core-tag__action-icon--feedback')}>
                <ActionIcon size={12} />
              </span>
            )}

            {renderChevron && (
              loading ? (
                <LoadingIndicator className="core-tag__loading-spinner" size="small" color="current" />
              ) : (
                <span className={cx('core-tag__chevron', dropdownOpen && 'core-tag__chevron--open')}>
                  <ChevronDown size={12} />
                </span>
              )
            )}
          </>
        ),
        children,
      )}

      {isOptionDropdown && hasContent && (
        <OptionDropdown
          isOpen={dropdownOpen}
          onClose={() => setDropdownOpen(false)}
          options={dropdownOptions || []}
          selectedItem={selectedItem}
          selectedItems={selectedItems}
          onSelectionChange={onSelectionChange}
          onMultiSelectionChange={onMultiSelectionChange}
          multiSelect={multiSelect}
          position={dropdownPosition}
          direction={dropdownDirection}
          variant={dropdownVariant}
          anchorRef={tagRef}
          renderFooter={allowUnselect}
          offsetY={dropdownOffsetY}
          maxHeight={dropdownMaxHeight}
          requireSelection={dropdownRequireSelection}
          defaultValue={dropdownDefaultValue}
        />
      )}

      {isDateDropdown && hasContent && (
        <DateDropdown
          isOpen={dropdownOpen}
          onClose={() => setDropdownOpen(false)}
          onDateRangeChange={(range) => {
            if (onDateRangeChange && range.startDate && range.endDate) {
              onDateRangeChange(range.startDate, range.endDate);
            }
          }}
          initialRange={startDate && endDate ? { startDate, endDate } : undefined}
          showPresets={showPresets}
          anchorRef={tagRef}
        />
      )}

      {isActionDropdown && hasContent && (
        <ActionDropdown
          isOpen={dropdownOpen}
          onClose={() => setDropdownOpen(false)}
          viewOptions={viewOptions}
          actionOptions={actionOptions}
          position={dropdownPosition}
          direction={dropdownDirection}
          anchorRef={tagRef}
          variant="strong"
        />
      )}
    </div>
  );
}
